











import Enumerable from "linq";
import { Component, Mixins, Prop, Emit, Watch } from "vue-property-decorator";
import { Point, Size, Rectangle } from "../../../../../../../base/domain/drawing";
import { DragKinds } from "../../../../../../../base/components/dragging";
import { TransformerFactory, ITransformable } from "../../../../../../../base/components/dragging/mixins";
import { StandardTransformer, AspectRatioFixedTransformer } from "../../../../../../../base/components/dragging/mixins/transformers";
import {
  FormatPageStructureEditor,
  IFormatObjectEditable,
} from "../../../../../../domain/editableFormat";
import {
  IRectangleCoordinateEditable, instanceOfIRectangleCoordinateEditable, instanceOfIPresentableFixedAspectRatio,
} from "../../../../../../domain/editableFormat/properties";
import { FormatPageMouseTrackableMixin } from "../mixins";

@Component
export default class RectangleCoordinatableObjectsTransformer extends Mixins(FormatPageMouseTrackableMixin) {
  @Prop()
  source: IFormatObjectEditable;

  @Prop()
  event: PointerEvent;

  @Prop()
  kind: DragKinds;

  @Emit()
  finished() {}

  data() {
    return {
      sourceRectangle: new Rectangle(new Point(0, 0), new Size(0, 0)),
      ratesFromCenter: new Rectangle(new Point(1, 1), new Size(1, 1)),
    };
  }
  sourceRectangle: Rectangle;
  ratesFromCenter: Rectangle;

  created(): void {
    if (!instanceOfIRectangleCoordinateEditable(this.source)) {
      this.$logger.warn("矩形描画対象外のオブジェクトからの要求です.実装を再確認してください.");
      return;
    }
    this.sourceRectangle = new Rectangle(Point.from(this.source.location().getValue()), Size.from(this.source.size().getValue()));
    this.ratesFromCenter = this.getRatesFromCenter(this.event);
  }

  handleMouseEntered(event: PointerEvent): void {
    this.onMouseEntered(this.pageElement, event);
    this.update(event);
  }

  handleMouseMoved(event: PointerEvent): void {
    this.onMouseMoved(this.pageElement, event);
    this.update(event);
  }

  handleMouseReleased(event: PointerEvent): void {
    this.onMouseReleased(this.pageElement, event);
    this.finish(true);
  }

  private update(event: PointerEvent): void {
    this.ratesFromCenter = this.getRatesFromCenter(event);
    this.writePropertyWhenValid(s => s.setOffsets({
      source: this.source,
      relocatingOffset: {
        x: 0,
        y: 0,
      },
      transformingRatesFromCenter: Rectangle.to(this.ratesFromCenter),
    }));
  }

  private getRatesFromCenter(event: PointerEvent): Rectangle {
    const transformed = this.createTransformer(event)
      .transformRectangle(this.sourceRectangle, this.toClientLocation(this.pageElement, event), this.kind);
    const center = this.sourceRectangle.center;
    const topLeftOffset = this.sourceRectangle.topLeft.subtract(center);
    const transformedTopLeftOffset = transformed.topLeft.subtract(center);
    return new Rectangle(
      new Point(
        transformedTopLeftOffset.x / topLeftOffset.x,
        transformedTopLeftOffset.y / topLeftOffset.y
      ),
      new Size(
        transformed.width / this.sourceRectangle.width,
        transformed.height / this.sourceRectangle.height
      )
    );
  }

  private createTransformer(event: PointerEvent): ITransformable {
    if (instanceOfIPresentableFixedAspectRatio(this.source) && this.source.isAspectRatioFixed()) {
      return new AspectRatioFixedTransformer(new StandardTransformer());
    }
    const factory = new TransformerFactory({
      shift: "aspectRatioFixed",
      control: "centerFixed",
      alt: "grid",
    });
    return factory.create(event);
  }

  private finish(shouldApply: boolean = false): void {
    this.writePropertyWhenValid(s => {
      const editor = s as FormatPageStructureEditor;
      editor.setOffsets();
      if (shouldApply) {
        Enumerable.from(editor.getObjects())
          .select(o => instanceOfIRectangleCoordinateEditable(o) ? o : null)
          .where(o => o && o.isSelected())
          .toArray()
          .forEach(o => this.transformObject(editor, o));
      }
    });
    this.finished();
  }

  private transformObject(editor: FormatPageStructureEditor, object: IFormatObjectEditable & IRectangleCoordinateEditable): void {
    const clone = object.clone() as IFormatObjectEditable & IRectangleCoordinateEditable;
    const rates = this.ratesFromCenter;
    const rectangle = new Rectangle(Point.from(clone.location().getValue()), Size.from(clone.size().getValue()));
    const center = rectangle.center;
    const topLeftOffset = rectangle.topLeft.subtract(center);
    const size = rectangle.size;
    clone.location().setValue(Point.to(center.add(new Point(topLeftOffset.x * rates.x, topLeftOffset.y * rates.y))));
    clone.size().setValue({
      width: size.width * rates.width,
      height: size.height * rates.height,
    });
    editor.updateObject(clone);
  }

  @Watch("isMouseTracking")
  onMouseTrackingChanged(newValue: boolean, oldValue: boolean): void {
    if (!newValue) {
      this.finish(false);
    }
  }
}
